import React, { useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { strings } from '/imports/strings';
import { css } from '/imports/ui/css';
import { isValidEmail } from '/imports/utils';
import { Modal, ModalStyled } from '/imports/ui/Modal';
import { Button } from '/imports/ui/Button';
import { Input } from '/imports/ui/Input';
import { Checkbox } from '/imports/ui/Checkbox';
import { toastOnError } from '/imports/ui/utils';
import { accountRoleLabel, sendAccountInvitation } from '/imports/api/accounts';

const InviteModalStyled = styled(ModalStyled)`
  .modal-window { width: 34rem; max-width: 100%; }
  .field { margin-bottom: 1.5rem; }
  .field > label { display: block; font-weight: 600; margin-bottom: 0.5rem; }
  .field.email .input, .field.email input { width: 100%; box-sizing: border-box; }
  /* The roles read as one control: a bordered grid, two columns, generous row spacing. */
  .roles-grid {
    display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.65rem 1.5rem;
    padding: 0.9rem 1rem; border: ${css.inputBorder}; border-radius: ${css.inputBorderRadius};
  }
  .roles-grid label { display: flex; align-items: center; gap: 0.6rem; margin: 0; font-weight: normal; cursor: pointer; }
  .hint { color: ${css.grey4}; font-size: 0.8rem; line-height: 1.4; margin: 0.6rem 0 0; }
  .modal-buttons { justify-content: flex-end; }
`;

interface Props {
  /** The roles the current user may grant — the server enforces the same rule, this just shapes the form. */
  grantableRoles: string[];
  onClose: () => void;
}

/** Invite someone into the current org by email; the recipient gets a signed link to finish signing up. */
export const InviteAccountModal = ({ grantableRoles, onClose }: Props) => {
  const [email, setEmail] = useState('');
  const [roles, setRoles] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const toggle = (role: string, on: boolean) => setRoles((prev) => (on ? [...new Set([...prev, role])] : prev.filter((r) => r !== role)));
  const send = toastOnError(async () => {
    setSending(true);
    try {
      await sendAccountInvitation.call({ email: email.trim(), roles });
      toast.success(strings.InvitationSent());
      onClose();
    }
    finally {
      setSending(false);
    }
  });
  return (
    <Modal
      styled={InviteModalStyled}
      title={strings.InviteToOrganization()}
      onClose={onClose}
      buttons={
        <>
          <Button className="secondary" onClick={onClose}>{strings.Cancel()}</Button>
          <Button className="primary" onClick={send} disabled={sending || !isValidEmail(email.trim())}>{strings.Send()}</Button>
        </>
      }
    >
      <div className="field email">
        <label htmlFor="invite-email">{strings.Email()}</label>
        <Input inputId="invite-email" value={email} onChange={(v) => setEmail(v || '')} placeholder={strings.Email()} autoFocus />
      </div>
      <div className="field">
        <label>{strings.Roles()}</label>
        <div className="roles-grid">
          {grantableRoles.map((role) => (
            <label key={role}>
              <Checkbox checked={roles.includes(role)} onChange={(on) => toggle(role, on)} />
              <span>{accountRoleLabel(role)}</span>
            </label>
          ))}
        </div>
        <p className="hint">{strings.InviteRolesHint()}</p>
      </div>
    </Modal>
  );
};
